import { ensureOk } from "@/api/apiError";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { components } from "@/api/generated-types";

export type ReferentialEntry = components["schemas"]["ReferentialEntryRead"];
export type ReferentialKind = "skill-categories" | "agent-use-cases";

async function fetchReferential(
  kind: ReferentialKind,
): Promise<ReferentialEntry[]> {
  const response = await httpClient(`/admin/referentials/${kind}`);
  await ensureOk(response);
  return response.json() as Promise<ReferentialEntry[]>;
}

async function addReferential(input: {
  kind: ReferentialKind;
  value: string;
}): Promise<ReferentialEntry> {
  const response = await httpClient(`/admin/referentials/${input.kind}`, {
    method: "POST",
    body: JSON.stringify({ value: input.value }),
  });
  await ensureOk(response);
  return response.json() as Promise<ReferentialEntry>;
}

async function removeReferential(input: {
  kind: ReferentialKind;
  value: string;
}): Promise<void> {
  const response = await httpClient(
    `/admin/referentials/${input.kind}/${encodeURIComponent(input.value)}`,
    { method: "DELETE" },
  );
  await ensureOk(response);
}

/** Admin referential (skill categories / agent use cases) — list. */
export function useReferential(kind: ReferentialKind) {
  return useQuery({
    queryKey: ["admin", "referentials", kind],
    queryFn: () => fetchReferential(kind),
    staleTime: 30 * 1000,
  });
}

export function useAddReferential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addReferential,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "referentials", variables.kind],
      });
    },
  });
}

export function useRemoveReferential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeReferential,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "referentials", variables.kind],
      });
    },
  });
}
